// ui/presenters/coverageAttention.js
import { COVERAGE_STATUS } from '../../domain/constants/coverage';
import { isVerified } from '../../domain/model/EligibilityVerification';
import { isReady } from '../../domain/services/policyholderReadiness';
import { coverageStatus } from '../../domain/services/coverageStatus';

/**
 * What a row says when something needs attention — and nothing when nothing does.
 *
 * PRESENTATION, not domain. The domain exposes four independent facts (the effective period,
 * `eligibilityVerified`, billing paused, and whether we hold enough about who pays) and has no reason
 * to prefer one over another: all of them are true at once. Collapsing them to a single value exists
 * purely because a row has room for one, which is a constraint of the screen, not of coverage.
 */
export const COVERAGE_ATTENTION = {
  ENDED: 'ended',
  ON_HOLD: 'on_hold',
  UPCOMING: 'upcoming',
  ELIGIBILITY_PENDING: 'eligibility_pending',
  NOT_READY: 'not_ready',
};

/**
 * The states that mean something is WRONG, as opposed to merely not yet.
 *
 * A row shows one warning icon for these and stays quiet otherwise. Upcoming is not a problem, it is
 * a coverage that starts next month, and flagging it the same way as a paused one teaches people to
 * ignore the icon. Ended is not a problem either, and it sits under a heading that already says so.
 */
export const ATTENTION_IS_A_PROBLEM = {
  [COVERAGE_ATTENTION.ENDED]: false,
  [COVERAGE_ATTENTION.UPCOMING]: false,
  [COVERAGE_ATTENTION.ON_HOLD]: true,
  [COVERAGE_ATTENTION.ELIGIBILITY_PENDING]: true,
  [COVERAGE_ATTENTION.NOT_READY]: true,
};

/**
 * The one thing to show, or null when we are billing on the coverage and it should stay quiet.
 *
 * Ordered by how strongly each fact explains why the coverage is not being billed:
 *
 *   1. ENDED               the period is over. Nothing else can change that.
 *   2. ON_HOLD             a deliberate park. It outranks UPCOMING because it survives the start
 *                          date arriving: waiting fixes UPCOMING, only a person clears this.
 *   3. UPCOMING            not started yet. Outranks the eligibility question because no amount
 *                          of verifying makes it billable before its start date.
 *   4. ELIGIBILITY_PENDING in effect, but we have not confirmed with the payer that it is active.
 *   5. NOT_READY           confirmed and in effect, but we do not hold enough about who pays.
 *                          Last on purpose: verifying can reveal the plan is not active at all, and
 *                          chasing a parent's date of birth for a coverage that turns out to be dead
 *                          is work nobody needed. Complete it once it is worth completing.
 *
 * A list where every row says something is a list nobody reads, which is why the billable case
 * returns null rather than a reassurance.
 *
 * @param {import('../../domain/model/Coverage.types').Coverage} coverage
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {boolean} [options.isBillingPaused] - a live BillingIntent against this coverage. Passed in
 *   because the pause is a separate aggregate, not a field on the coverage.
 * @param {Object|null} [options.policyholderDemographics] - whoever holds the policy. Passed in for
 *   the same reason: they are another record, not a field on this one. Null while it is being read,
 *   which reports nothing rather than reporting a gap that may not exist.
 * @returns {string|null} COVERAGE_ATTENTION, or null when there is nothing to flag
 */
export function coverageAttention(
  coverage,
  asOf,
  { isBillingPaused = false, policyholderDemographics = null } = {},
) {
  const status = coverageStatus(coverage, asOf);

  if (status === COVERAGE_STATUS.ENDED) {
    return COVERAGE_ATTENTION.ENDED;
  }
  if (isBillingPaused === true) {
    return COVERAGE_ATTENTION.ON_HOLD;
  }
  if (status === COVERAGE_STATUS.UPCOMING) {
    return COVERAGE_ATTENTION.UPCOMING;
  }
  if (isVerified(coverage.eligibilityVerification) !== true) {
    return COVERAGE_ATTENTION.ELIGIBILITY_PENDING;
  }
  if (!isReady(coverage, policyholderDemographics)) {
    return COVERAGE_ATTENTION.NOT_READY;
  }
  return null;
} // coverageAttention()
